import React, { useCallback, useMemo, useState } from 'react';
import QuoteLaneEditorDrawer from '../pages/order-management/components/QuoteLaneEditorDrawer';
import { type QuoteListRow } from '../pages/order-management/fixtures';
import QuoteDrawerContext from './QuoteDrawerContext';

/**
 * Mounts the one shared quote drawer and supplies the opener every quote-reference cell
 * calls. See QuoteDrawerContext for why the context lives in its own module.
 */
export const QuoteDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState<{ quoteNumber: number; revisionNumber: number } | null>(null);

  const openQuote = useCallback((quoteNumber: number, revisionNumber: number) => {
    // Both halves or nothing: a quote without a revision names no revision row, so the
    // drawer would open on an empty quote.
    if (!quoteNumber || !revisionNumber) return;
    setOpen({ quoteNumber, revisionNumber });
  }, []);

  const value = useMemo(() => ({ openQuote, available: true }), [openQuote]);

  // The drawer takes a lane row because that is what the lane table hands it, but it
  // loads the quote from the number/revision pair alone — the same minimal lane the
  // order's Invoicing tab builds (InvoicingView.tsx:1794).
  const lane = open
    ? ({
        quoteId: `${open.quoteNumber}-${open.revisionNumber}`,
        quoteNumber: open.quoteNumber,
        revisionNumber: open.revisionNumber,
        quoteType: 'DIRECT',
        customer: '', billTo: '', lane: '', csr: '', effective: '', expires: '',
        pros: 0, status: 'UNSECURED', hot: false,
      } as QuoteListRow)
    : null;

  return (
    <QuoteDrawerContext.Provider value={value}>
      {children}
      {/* No `onChanged`: the grids that link here show ORDER data. An edit made in the
          drawer changes the quote, not the rows behind it, so refetching them would
          redraw identical rows and cost the user their scroll position. */}
      <QuoteLaneEditorDrawer open={open !== null} lane={lane} onClose={() => setOpen(null)} />
    </QuoteDrawerContext.Provider>
  );
};

export default QuoteDrawerProvider;
